/**
 * Customer account drawer — profil + geçmiş siparişler + loyalty.
 *
 * Customer JWT (localStorage.customer_token) gerekir. Zenginleştirilmiş:
 * üst profil kartı (avatar + düzenle), tier ilerleme çubuğu, çıkış butonu.
 */
import {
  Drawer, Stack, Card, Group, Text, Badge, Tabs, Loader, Center, Title,
  Divider, Avatar, Button, TextInput, FileButton, Progress, ActionIcon,
} from "@mantine/core";
import {
  IconReceipt, IconCoin, IconLogout, IconEdit, IconCamera, IconCheck, IconX,
} from "@tabler/icons-react";
import axios from "axios";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";

// VITE_API_URL tanımlı değilse backend default'una düş (apiClient ile aynı).
// Boş string bırakılırsa /me/* relative kalır → vite dev server'a (5176)
// gider, backend'e (8000) DEĞİL → SPA fallback HTML döner, puanlar boş görünür.
const API_URL = import.meta.env["VITE_API_URL"] || "http://localhost:8000";

function customerAuth() {
  const token = localStorage.getItem("customer_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatTRY(minor: number | null | undefined): string {
  const safe = typeof minor === "number" && !Number.isNaN(minor) ? minor : 0;
  return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(safe / 100);
}

// Tier eşikleri (lifetime kazanılan puan, kuruş). Backend default'larıyla
// uyumlu (core/settings/registry loyalty_tier_thresholds). İlerleme çubuğu
// için bir sonraki seviyeyi hesaplar.
const TIERS: Array<{ key: string; label: string; min: number }> = [
  { key: "BRONZE", label: "Bronz", min: 0 },
  { key: "SILVER", label: "Gümüş", min: 50000 },
  { key: "GOLD", label: "Altın", min: 200000 },
  { key: "PLATINUM", label: "Platin", min: 1000000 },
];

function tierProgress(lifetimeMinor: number) {
  const lifetime = typeof lifetimeMinor === "number" && !Number.isNaN(lifetimeMinor) ? lifetimeMinor : 0;
  let current = TIERS[0];
  let next: typeof TIERS[number] | null = null;
  for (let i = 0; i < TIERS.length; i++) {
    if (lifetime >= TIERS[i].min) {
      current = TIERS[i];
      next = TIERS[i + 1] ?? null;
    }
  }
  if (!next) return { current, next: null, percent: 100, remainingMinor: 0 };
  const span = next.min - current.min;
  const done = lifetime - current.min;
  const percent = Math.max(0, Math.min(100, Math.round((done / span) * 100)));
  return { current, next, percent, remainingMinor: Math.max(0, next.min - lifetime) };
}

interface Props {
  opened: boolean;
  onClose: () => void;
  customerName: string;
  onLogout: () => void;
  onProfileUpdate?: (p: { display_name: string; avatar: string | null }) => void;
}

export function MyAccountDrawer({ opened, onClose, customerName, onLogout, onProfileUpdate }: Props) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ["me-profile"],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/me/profile/`, { headers: customerAuth() });
      return data;
    },
    enabled: opened,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const { data: orders, isLoading: ordersLoading } = useQuery({
    queryKey: ["me-orders"],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/me/orders/`, { headers: customerAuth() });
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.results)) return data.results;
      return [];
    },
    enabled: opened,
    // Drawer her açıldığında taze çek — puan/sipariş sonradan kazanılır,
    // cache eski ₺0'ı göstermesin.
    staleTime: 0,
    refetchOnMount: "always",
  });

  const { data: loyalty, isLoading: loyaltyLoading } = useQuery({
    queryKey: ["me-loyalty"],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/me/loyalty/`, { headers: customerAuth() });
      return data;
    },
    enabled: opened,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const displayName = profile?.display_name || customerName || "Misafir";
  const initial = (displayName?.[0] || "?").toUpperCase();

  function startEdit() {
    setEditName(profile?.display_name || "");
    setEditEmail(profile?.email || "");
    setAvatarFile(null);
    setEditing(true);
  }

  async function saveProfile() {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("display_name", editName);
      fd.append("email", editEmail);
      if (avatarFile) fd.append("avatar", avatarFile);
      const { data } = await axios.patch(`${API_URL}/me/profile/`, fd, { headers: customerAuth() });
      notifications.show({ message: "Profil güncellendi", color: "green" });
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["me-profile"] });
      // Üst bar avatar/ad'ı da güncellensin (App session state).
      onProfileUpdate?.({ display_name: data.display_name, avatar: data.avatar });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Güncellenemedi", color: "red" });
    } finally {
      setSaving(false);
    }
  }

  const prog = tierProgress(loyalty?.lifetime_earned_minor ?? 0);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      title="Hesabım"
    >
      <Stack gap="md" h="100%">
        {/* Profil kartı */}
        <Card padding="md" radius="md" withBorder>
          {editing ? (
            <Stack gap="sm">
              <Group justify="center">
                <Avatar
                  size={72}
                  radius="xl"
                  color="blue"
                  src={avatarFile ? URL.createObjectURL(avatarFile) : profile?.avatar || null}
                  imageProps={{ onError: (e: any) => { e.currentTarget.style.display = "none"; } }}
                >
                  {initial}
                </Avatar>
                <FileButton onChange={setAvatarFile} accept="image/png,image/jpeg,image/webp">
                  {(props) => (
                    <Button {...props} variant="light" size="xs" leftSection={<IconCamera size={14} />}>
                      Foto Seç
                    </Button>
                  )}
                </FileButton>
              </Group>
              <TextInput
                label="Ad Soyad"
                value={editName}
                onChange={(e) => setEditName(e.currentTarget.value)}
                placeholder="Adınız"
              />
              <TextInput
                label="E-posta"
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.currentTarget.value)}
                placeholder="ornek@mail.com"
              />
              <Group justify="flex-end" gap="xs">
                <Button variant="subtle" color="gray" size="xs" leftSection={<IconX size={14} />} onClick={() => setEditing(false)}>
                  Vazgeç
                </Button>
                <Button size="xs" loading={saving} leftSection={<IconCheck size={14} />} onClick={saveProfile}>
                  Kaydet
                </Button>
              </Group>
            </Stack>
          ) : (
            <Group justify="space-between" wrap="nowrap">
              <Group wrap="nowrap">
                <Avatar
                  size={56}
                  radius="xl"
                  src={profile?.avatar || null}
                  color="blue"
                  imageProps={{ onError: (e: any) => { e.currentTarget.style.display = "none"; } }}
                >
                  {initial}
                </Avatar>
                <Stack gap={0}>
                  <Text fw={700} size="md">{displayName}</Text>
                  {profile?.phone && <Text size="xs" c="dimmed">{profile.phone}</Text>}
                  {profile?.email && <Text size="xs" c="dimmed">{profile.email}</Text>}
                </Stack>
              </Group>
              <ActionIcon variant="light" onClick={startEdit} aria-label="Düzenle">
                <IconEdit size={18} />
              </ActionIcon>
            </Group>
          )}
        </Card>

        <Tabs defaultValue="loyalty">
          <Tabs.List>
            <Tabs.Tab value="loyalty" leftSection={<IconCoin size={14} />}>
              Puanlar
            </Tabs.Tab>
            <Tabs.Tab value="orders" leftSection={<IconReceipt size={14} />}>
              Siparişlerim
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="loyalty" pt="md">
            {loyaltyLoading ? <Center h={200}><Loader /></Center> : loyalty ? (
              <Stack gap="md">
                <Card padding="lg" radius="md" withBorder style={{ background: "linear-gradient(135deg,#fff9db,#fff3bf)" }}>
                  <Stack gap={6} align="center">
                    <Text size="xs" c="dimmed">Puan Bakiyeniz</Text>
                    <Title order={1} c="orange.7">{formatTRY(loyalty.balance_minor)}</Title>
                    <Badge size="lg" color="yellow">{prog.current.label}</Badge>
                    <Text size="xs" c="dimmed">Toplam kazanılan: {formatTRY(loyalty.lifetime_earned_minor)}</Text>
                  </Stack>
                </Card>

                {/* Tier ilerleme çubuğu */}
                {prog.next ? (
                  <Card padding="md" radius="md" withBorder>
                    <Stack gap={6}>
                      <Group justify="space-between">
                        <Text size="sm" fw={600}>{prog.current.label}</Text>
                        <Text size="sm" c="dimmed">{prog.next.label}</Text>
                      </Group>
                      <Progress value={prog.percent} color="yellow" size="lg" radius="xl" />
                      <Text size="xs" c="dimmed" ta="center">
                        {prog.next.label} seviyesine {formatTRY(prog.remainingMinor)} kaldı
                      </Text>
                    </Stack>
                  </Card>
                ) : (
                  <Card padding="md" radius="md" withBorder>
                    <Text size="sm" ta="center" c="yellow.8" fw={600}>🏆 En yüksek seviyedesiniz!</Text>
                  </Card>
                )}

                {loyalty.badges?.length > 0 && (
                  <>
                    <Title order={5}>🏅 Rozetler</Title>
                    <Group>
                      {loyalty.badges.map((b: any) => (
                        <Badge key={b.id} size="lg" variant="filled" color="yellow">{b.badge_name}</Badge>
                      ))}
                    </Group>
                  </>
                )}

                <Divider />

                <Title order={5}>Son İşlemler</Title>
                <Stack gap="xs">
                  {(loyalty.transactions || []).slice(0, 10).map((t: any) => (
                    <Group key={t.id} justify="space-between">
                      <Stack gap={0}>
                        <Text size="sm">{t.transaction_type}</Text>
                        <Text size="xs" c="dimmed">{new Date(t.created_at).toLocaleString("tr-TR")}</Text>
                      </Stack>
                      <Text fw={600} c={t.delta_minor >= 0 ? "green" : "red"}>
                        {t.delta_minor >= 0 ? "+" : ""}{formatTRY(t.delta_minor)}
                      </Text>
                    </Group>
                  ))}
                  {(loyalty.transactions || []).length === 0 && <Text c="dimmed" size="sm">İşlem yok</Text>}
                </Stack>
              </Stack>
            ) : <Text c="dimmed">Yükleniyor...</Text>}
          </Tabs.Panel>

          <Tabs.Panel value="orders" pt="md">
            {ordersLoading ? <Center h={200}><Loader /></Center> : (
              <Stack gap="sm">
                {(orders || []).length === 0 && <Text c="dimmed" ta="center">Henüz siparişiniz yok</Text>}
                {(orders || []).slice(0, 5).map((o: any) => (
                  <Card key={o.id} padding="sm" radius="md" withBorder>
                    <Group justify="space-between">
                      <Stack gap={2}>
                        <Text fw={600} size="sm">{o.table_name || "Masa"} • {new Date(o.created_at).toLocaleString("tr-TR")}</Text>
                        <Group gap="xs">
                          <Badge size="xs" variant="light">{o.source}</Badge>
                          <Badge size="xs">{o.kitchen_status}</Badge>
                        </Group>
                      </Stack>
                      <Text fw={700} c="blue">{formatTRY(o.total_minor)}</Text>
                    </Group>
                    {(o.lines || []).length > 0 && (
                      <Stack gap={2} mt="xs">
                        {o.lines.map((line: any, i: number) => (
                          <Group key={i} justify="space-between" wrap="nowrap">
                            <Text size="xs" c="dimmed">{line.quantity}× {line.product_name}</Text>
                            <Text size="xs" c="dimmed">{formatTRY(line.line_total_minor)}</Text>
                          </Group>
                        ))}
                      </Stack>
                    )}
                  </Card>
                ))}
              </Stack>
            )}
          </Tabs.Panel>
        </Tabs>

        {/* Çıkış butonu — en altta (UX standardı) */}
        <Divider mt="auto" />
        <Button
          variant="light"
          color="red"
          leftSection={<IconLogout size={18} />}
          onClick={() => { onLogout(); onClose(); }}
          fullWidth
        >
          Çıkış Yap
        </Button>
      </Stack>
    </Drawer>
  );
}
